import React, { useEffect, useState } from 'react'
import { displayAssignee, mutationErrorMessage } from './conductorUtils'
import {
  useClaimConductorWorkItemMutation,
  useGetConductorRoleAssigneesQuery,
  useReassignConductorWorkItemMutation,
  useUnclaimConductorWorkItemMutation,
} from './conductorApi'

import type { ConductorAssignResult, ConductorWorkItem } from './conductor.types'

const OPEN_STATUSES = ['OPEN', 'IN_PROGRESS'] as const

const buttonClasses =
  'rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'

const selectClasses =
  'rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

const outcomeLabel = (outcome: ConductorAssignResult['outcome']): string => {
  if (outcome === 'NOT_ELIGIBLE') {
    return 'Could not change assignee (not in the role directory)'
  }
  if (outcome === 'CONFLICT') {
    return 'Someone else changed the assignee. Refresh and try again'
  }
  if (outcome === 'NOT_OPEN') {
    return 'This item is no longer open'
  }
  if (outcome === 'NOOP') {
    return 'Already in that state'
  }
  return 'Updated'
}

export const WorkItemAssignmentActions: React.FC<{
  item: ConductorWorkItem
  currentUserEmail: string
}> = ({ item, currentUserEmail }) => {
  const isOpen = OPEN_STATUSES.includes(item.status as (typeof OPEN_STATUSES)[number])
  const callerEmail = currentUserEmail.trim()
  const [target, setTarget] = useState('')
  const [outcome, setOutcome] = useState<ConductorAssignResult['outcome'] | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [localAssignee, setLocalAssignee] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    setLocalAssignee(undefined)
  }, [item.assigneeUserId])

  const assigneesQuery = useGetConductorRoleAssigneesQuery(item.ownerRole, {
    skip: !isOpen || callerEmail === '',
  })
  const [claim, claimState] = useClaimConductorWorkItemMutation()
  const [unclaim, unclaimState] = useUnclaimConductorWorkItemMutation()
  const [reassign, reassignState] = useReassignConductorWorkItemMutation()

  if (!isOpen || callerEmail === '') {
    return null
  }

  const busy = claimState.isLoading || unclaimState.isLoading || reassignState.isLoading
  const assignee = localAssignee !== undefined ? localAssignee : item.assigneeUserId
  const mine = assignee === callerEmail

  const run = async (fn: () => Promise<ConductorAssignResult>): Promise<void> => {
    setMutationError(null)
    setOutcome(null)
    try {
      const result = await fn()
      setOutcome(result.outcome)
      if (result.outcome === 'APPLIED' || result.outcome === 'NOOP') {
        setLocalAssignee(result.assigneeUserId)
      }
    } catch (error) {
      setMutationError(mutationErrorMessage(error))
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {!mine && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(async () => claim(item.cuid).unwrap())}
            className={buttonClasses}
          >
            Claim
          </button>
        )}
        {mine && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(async () => unclaim(item.cuid).unwrap())}
            className={buttonClasses}
          >
            Unclaim
          </button>
        )}
        <label className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          Reassign
          <select
            aria-label={`Reassign ${item.title}`}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className={selectClasses}
          >
            <option value="">Choose person</option>
            {(assigneesQuery.data ?? []).map((row) => (
              <option key={row.userId} value={row.userId}>
                {row.userLabel ?? displayAssignee(row.userId)}
                {row.isPrimary ? ' (primary)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={busy || target === ''}
          onClick={() => void run(async () => reassign({ cuid: item.cuid, assigneeUserId: target }).unwrap())}
          className={buttonClasses}
        >
          Assign
        </button>
      </div>
      {assigneesQuery.isError && (
        <div className="text-xs text-red-600 dark:text-red-400">Could not load the role directory</div>
      )}
      {outcome != null && (
        <div className="text-xs text-slate-500 dark:text-slate-400">{outcomeLabel(outcome)}</div>
      )}
      {mutationError != null && <div className="text-xs text-red-600 dark:text-red-400">{mutationError}</div>}
    </div>
  )
}

export default WorkItemAssignmentActions
